import {ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {debounceTime, finalize, switchMap, tap} from 'rxjs/operators';
import {isNil} from 'lodash-es';
import {PermissionService} from '../../../core/permission.service';
import {Observable, of} from 'rxjs';
import {marker} from "@biesbjerg/ngx-translate-extract-marker";

@Component({
    selector: 'app-generic-autocomplete',
    templateUrl: './generic-autocomplete.component.html',
    styleUrls: ['./generic-autocomplete.component.scss'],
    standalone: false
})
export class GenericAutocompleteComponent<T> implements OnInit {

  @Input() formGroup: FormGroup;
  @Input() autocompleteControlName: string;
  @Input() maxLength = 100;
  @Input() label: string;
  @Input() labelAsObs: Observable<string>;
  @Input() errorRequiredMessage: string;
  @Input() showNoMatchFound = true;
  @Input() autoselectSingleItem = true;
  @Input() noMatchFoundMessage: string = marker('No match found.');

  @Input() required = true;
  @Input() autocompleteDisabled = false;
  @Input() appearance = 'standard';

  @Input() displayField = 'name';
  @Input() customDisplayFunction: (args: T) => string;
  @Input() showOptionTooltips = false;
  @Input() filterPermission: string;
  @Input() filterFunction: (param:string) => Observable<T[]>;

  @Input() inactive = false;
  @Input() inactiveMessage: string;
  @Input() id: string;

  @Input() customErrorKey: string;
  @Input() customErrorMessage: string;
  @Input() autoCompleteMinChars: number;

  noMatchFound = false;
  _selected?: string;
  canFilterItems = true;

  @Input('selected')
  set selected(selectedValue: string) {
    this._selected = selectedValue;

    this.formGroup?.get(this.autocompleteControlName)?.setValue(selectedValue);
  }

  @Output() valueChanged = new EventEmitter<T>();

  filteredValues: T[] = [];
  isLoading = false;

  constructor(
    private fb: FormBuilder,
    private permissionService: PermissionService,
    public changeDetector: ChangeDetectorRef,
  ) {
  }

  ngOnInit() {
    const validators = this.required ? [Validators.required] : [];
    this.formGroup.addControl(this.autocompleteControlName, this.fb.control(!isNil(this._selected) ? this._selected : '', validators));

    if(!isNil(this.filterPermission)) {
      this.permissionService.hasPermission(this.filterPermission).subscribe(perm => {
        if(perm === true) {
          this.filterItemsOnFieldChanges();
        } else {
          this.canFilterItems = false;
        }
      });
    } else {
      this.filterItemsOnFieldChanges();
    }

    if (this.autocompleteDisabled) {
      this.formGroup.get(this.autocompleteControlName).disable();
    }
  }

  private filterItemsOnFieldChanges() {
    this.formGroup
      .get(this.autocompleteControlName)
      .valueChanges
      .pipe(
        debounceTime(300),
        tap(() => {
          this.isLoading = true;
          this.noMatchFound = false;
        }),
        switchMap(value => {
            if(!this._selected) {
              if (this.enoughAutocompleteChars()) {
                return this.filterFunction(value)
                  .pipe(
                    finalize(() => this.isLoading = false),
                  )
              } else {
                return of(undefined).pipe(
                  finalize(() => this.isLoading = false),
                )
              }
            } else {
              //don't search again once we set the value
              return of(undefined).pipe(
                finalize(() => this.isLoading = false),
              )
            }
          }
        ),
      )
      .subscribe(response => {
        if (response === undefined) {
          this._selected = null;
        }
        if (this.formGroup.get(this.autocompleteControlName).value === '') {
          this.valueChanged.emit(undefined);
        }
        this.filteredValues = response;

        if (this.filteredValues && this.filteredValues.length === 0 && ( typeof this.formGroup.get(this.autocompleteControlName).value === 'string')) {
          this.noMatchFound = true;
        }
        if (this.autoselectSingleItem && this.filteredValues && this.filteredValues.length === 1) {

          //if a single option, auto select it
          this.formGroup.get(this.autocompleteControlName).setValue(this.filteredValues[0]);
          this.onItemSelectedInternal(this.filteredValues[0]);
        }
      });
  }

  displayItemField(itemOrDisplayName: any) {
    if ( !isNil(itemOrDisplayName) && itemOrDisplayName.hasOwnProperty(this.displayField)) {
      // item returned by autocomplete
      return itemOrDisplayName[this.displayField];
    }

    return itemOrDisplayName;
  }

  onItemSelectedInternal(item: T) {
    this._selected = item[this.displayField];
    this.valueChanged.emit(item);
  }

  private enoughAutocompleteChars() {
    const typedValue = this.formGroup.get(this.autocompleteControlName).value;
    return !this.autoCompleteMinChars || typedValue && typedValue.length >= this.autoCompleteMinChars;
  }

  displayFunction(item: T) {
    if (this.customDisplayFunction != null) {
      return this.customDisplayFunction(item);
    }
    return item[this.displayField];
  }
}
